import { BuildingData } from './building-interfaces';

// Shapes for the Organization -> Portfolio -> Building -> Unit hierarchy and the
// occupancy / access-grant records hanging off it.
//
// Every field below is copied from what the backend ACTUALLY serializes
// (src/CRUD/portfolios.js, units.js, occupancies.js, staff-scopes.js), not from
// the DB table definition. The two differ on purpose in several places — the
// routers project a subset and add derived fields — and a field that exists in
// Postgres but never leaves the router would read as `undefined` here with no
// error to point at it.

// DATEONLY columns (moved_in, moved_out, lease_start, lease_end) travel as
// 'YYYY-MM-DD' strings end to end; the routers parse and re-emit them in that
// form rather than as Date objects.
export type IsoDate = string;

// Sequelize `timestamps: true` columns and access_grants.valid_from/valid_until
// (timestamptz) serialize as full ISO-8601 strings.
export type IsoDateTime = string;

// ---------------------------------------------------------------------------
// Enumerations — mirrors of the DB CHECK constraints, revalidated in JS by the
// routers. Kept as unions so a typo in a filter or a form value is a compile
// error instead of a 400 at runtime.
// ---------------------------------------------------------------------------

// units.kind — chk_unit_kind. 'unassigned' is a placeholder unit the 018
// migration minted for residents who had no unit number: real rows that need
// attention, not rows to hide.
export const UNIT_KINDS = [
  'residential',
  'commercial',
  'common',
  'parking',
  'storage',
  'unassigned',
] as const;

export type UnitKind = (typeof UNIT_KINDS)[number];

// units.status. 'archived' rows are excluded from the partial unique index on
// (building_id, unit_number_norm), which is why archiving frees the number for
// reuse while the occupancy history survives.
export const UNIT_STATUSES = ['active', 'offline', 'archived'] as const;

export type UnitStatus = (typeof UNIT_STATUSES)[number];

// occupancies.relationship — chk_occ_rel.
export const OCCUPANCY_RELATIONSHIPS = [
  'resident',
  'co_resident',
  'occupant',
  'guarantor',
  'authorized',
] as const;

export type OccupancyRelationship = (typeof OCCUPANCY_RELATIONSHIPS)[number];

// access_grants.scope_type — chk_ag_type. chk_ag_shape then requires the
// matching id column and forbids the other two; 'organization' names none.
export const SCOPE_TYPES = [
  'organization',
  'portfolio',
  'building',
  'unit',
] as const;

export type ScopeType = (typeof SCOPE_TYPES)[number];

// access_grants.reason — chk_ag_reason.
export const GRANT_REASONS = [
  'assignment',
  'coverage',
  'training',
  'temporary',
] as const;

export type GrantReason = (typeof GRANT_REASONS)[number];

// portfolios.status.
export type PortfolioStatus = 'active' | 'archived';

// ---------------------------------------------------------------------------
// Portfolio
// ---------------------------------------------------------------------------

// GET /portfolios (list row) and the base of GET /portfolios/:id.
//
// building_count counts buildings of EVERY status, matching the RESTRICT FK,
// which blocks a delete on the row's existence rather than on its status. A
// count that ignored inactive buildings would show "0 buildings" beside a
// portfolio that then refuses to delete.
export interface Portfolio {
  id: number;
  name: string;
  code: string | null;
  description: string | null;
  // Exactly one per org (uq_pf_default) and it cannot be deleted — it is the
  // fallback a building lands in when its create names no portfolio.
  is_default: boolean;
  status: PortfolioStatus;
  building_count: number;
  // Units rolled up from every building in the portfolio — a portfolio groups
  // buildings, and a building groups units, so this is what the group is
  // actually worth at a glance. Counted the way billing counts: active units in
  // active buildings. Absent (not 0) on responses that did not compute it, so
  // "none" stays distinguishable from "not counted".
  unit_count?: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

// The building projection nested inside GET /portfolios/:id.
//
// Reuses the canonical field types from BuildingData rather than restating
// them. `id` is redeclared as a number: BuildingData types it as a string for
// the legacy /dashboard/buildings screens, but properties.id is INTEGER and
// this endpoint serializes the raw column, so JSON carries a number here.
export interface PortfolioBuilding
  extends Pick<BuildingData, 'property_name' | 'address' | 'country'> {
  id: number;
  // Declared inline rather than picked from BuildingData: that interface types
  // `city?: string` for the legacy building forms, which the server's explicit
  // `city: null` does not satisfy.
  city: string | null;
  status: string | null;
  timezone: string | null;
  // Which portfolio the building is in right now — echoed so a move dialog can
  // exclude the current portfolio from the destination list without a second
  // request.
  portfolio_id: number;
  // The building's cover photo, presigned and short-lived. The SAME field the
  // buildings list serves (see BuildingData.cover_url) and from the same source
  // — the entity_images row flagged is_primary. Optional because it is absent
  // on any response that did not compute it, and null when the building has no
  // photos at all; neither is an error.
  cover_url?: string | null;
}

// GET /portfolios/:id — the portfolio plus the buildings inside it.
export interface PortfolioDetail extends Portfolio {
  buildings: PortfolioBuilding[];
}

// GET /portfolios/buildings — every building in the org, each stamped with the
// portfolio holding it. One request, so a picker never fans out over
// getPortfolio() per portfolio.
//
// Buildings of every status appear: an inactive building still occupies a
// portfolio and still blocks archive and delete, so hiding it would make a
// portfolio that refuses to archive look empty.
export interface PortfolioBuildingIndexRow extends PortfolioBuilding {
  // Resolved from the org's FULL portfolio set, so a building parked in an
  // archived portfolio still renders under a real heading.
  portfolio_name: string | null;
  // Lets a picker float the default portfolio's group to the top, matching the
  // is_default DESC ordering GET /portfolios already returns.
  portfolio_is_default: boolean;
  // How many units travel with this building if it is moved.
  units_count: number;
}

// Which portfolios a list route should return. Defaults to 'active' server-side
// — an archived portfolio is one the owner put away, and no picker should offer
// it. The Portfolios screen itself asks for 'all' so Restore stays reachable.
export type PortfolioStatusFilter = 'active' | 'archived' | 'all';

// One staff member whose access to the moved building changed. The server
// resolves the name inside the request (users.full_name is encrypted at rest);
// it is null when the id no longer resolves to a member of the organisation,
// which is still a real access change and is deliberately not dropped.
export interface AccessDeltaMember {
  user_id: string;
  name: string | null;
  role: string | null;
}

// PUT /buildings/:id/portfolio — moving one building.
//
// `moved` is false when the building was already there. The route deliberately
// skips the UPDATE in that case (the access-grant trigger fires on the column
// being MENTIONED, not on the value changing), so previous_* are absent.
//
// `gained` / `lost` are the ACCESS DELTA: moving a building re-expands every
// org- and portfolio-scoped grant, so a manager scoped to the destination
// portfolio starts seeing it and one scoped to the source stops. Both lists are
// always present and are empty when nothing changed.
//
// `scope_enforced` says whether that delta MEANS anything. In an organisation
// with scope enforcement off, the access rows are accurate but never consulted,
// so announcing "one person lost access" would be false. Gate the wording on it.
export interface BuildingPortfolioMove {
  moved: boolean;
  building_id: number;
  portfolio_id: number;
  portfolio_name: string;
  previous_portfolio_id?: number | null;
  previous_portfolio_name?: string | null;
  scope_enforced: boolean;
  gained: AccessDeltaMember[];
  lost: AccessDeltaMember[];
}

// One building's share of a bulk move, so the audit trail and this response can
// both answer "which building cost this person their access" — `previous` below
// is a COUNT map and never could.
export interface PortfolioBuildingMoveDetail {
  building_id: number;
  from_portfolio_id: number;
  from_portfolio_name: string | null;
  access_gained: string[];
  access_lost: string[];
}

// POST /portfolios/:id/buildings — the destination portfolio after a bulk move,
// plus how many buildings actually changed hands and where they came from
// (portfolio id -> count).
//
// `gained` / `lost` are the UNION across the batch: a person can appear in both
// when one building brought them access and another took it away.
export interface PortfolioBuildingsMoved extends PortfolioDetail {
  moved: number;
  previous: Record<string, number>;
  scope_enforced: boolean;
  moves: PortfolioBuildingMoveDetail[];
  gained: AccessDeltaMember[];
  lost: AccessDeltaMember[];
}

export interface CreatePortfolioInput {
  name: string;
  code?: string | null;
  description?: string | null;
}

// Every field optional: PUT /portfolios/:id patches only what it is given and
// rejects a body with no updatable field at all (400). organization_id,
// is_default and status are not writable here and are ignored if sent — they
// move through their own action endpoints (archive / unarchive / default), so
// each carries its own preconditions and its own audit entry.
export type UpdatePortfolioInput = Partial<CreatePortfolioInput>;

// ---------------------------------------------------------------------------
// Unit
// ---------------------------------------------------------------------------

export interface Unit {
  id: number;
  building_id: number;
  unit_number: string;
  // Optional custom label on top of unit_number (migration 033). The number
  // stays the identifier; this is the human name the building uses for the door
  // ("Penthouse", "Manager's flat"). null when the unit is just its number.
  name: string | null;
  // STORED GENERATED column: upper(replace(btrim(unit_number),' ','')).
  // Read-only — Postgres rejects any write naming it. Exposed so the client can
  // sort and dedupe on the same key the unique index uses ('4b' and '4 B' both
  // normalize to '4B').
  unit_number_norm: string | null;
  kind: UnitKind;
  floor: string | null;
  bedrooms: number | null;
  // numeric(3,1). node-postgres returns numeric as a STRING to avoid float
  // precision loss and the router passes it through untouched, so this is
  // '1.5', not 1.5. Do not do arithmetic on it without parsing first.
  bathrooms: string | null;
  sq_ft: number | null;
  status: UnitStatus;
  notes: string | null;
}

// Vacancy is DERIVED from the count of open occupancies (moved_out IS NULL) on
// every request rather than stored on the unit, so it cannot drift.
export interface UnitOccupancyState {
  occupant_count: number;
  is_vacant: boolean;
}

// GET /buildings/:buildingId/units row.
export interface UnitListItem extends Unit, UnitOccupancyState {}

// The occupant projection nested in GET /units/:id.
//
// Deliberately NARROWER than Occupancy: this endpoint returns only CURRENT
// occupants, so it omits moved_out, is_current, move_out_reason, unit_id and
// building_id. Typing it as a full Occupancy would make `moved_out` look
// readable when the key is simply absent.
export interface UnitOccupant {
  id: string;
  // NULLABLE on purpose: a spouse, a minor or a short-term occupant with no
  // login is still a real resident the front desk must be able to look up.
  user_id: string | null;
  first_name: string;
  last_name: string;
  relationship: OccupancyRelationship;
  is_primary: boolean;
  moved_in: IsoDate;
  lease_start: IsoDate | null;
  lease_end: IsoDate | null;
}

// GET /units/:id
export interface UnitDetail extends Unit, UnitOccupancyState {
  occupants: UnitOccupant[];
  // Today's calendar date in the BUILDING's zone (Plan 7): the default and the
  // latest date of the move-in / move-out forms. Optional: an older server.
  building_today?: IsoDate;
}

// What happened to a leaving resident's visitor passes (Plan 7, migration
// 127): moved to whoever stays in the unit, or ended when nobody does.
export interface OccupancyPassesOutcome {
  moved: number;
  ended: number;
  moved_to: string | null;
}

// POST /buildings/:buildingId/units. `kind` defaults to 'residential'.
// bathrooms accepts a number on the way in even though it reads back as a
// string. building_id and organization_id are never accepted from the client.
export interface CreateUnitInput {
  unit_number: string;
  // Optional custom label. Send null to clear it on a PUT; '' is coerced to null
  // by the router either way.
  name?: string | null;
  kind?: UnitKind;
  floor?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | string | null;
  sq_ft?: number | null;
  notes?: string | null;
}

// PUT /units/:id. status becomes editable here; building_id stays immutable —
// moving a unit between buildings would silently relocate every occupancy and
// access grant hanging off it.
export interface UpdateUnitInput extends Partial<CreateUnitInput> {
  status?: UnitStatus;
}

export interface ListUnitsOptions {
  // Only units with no open occupancy.
  vacant?: boolean;
  // Archived units are hidden unless this is set.
  includeArchived?: boolean;
  kind?: UnitKind;
}

// ---------------------------------------------------------------------------
// Occupancy
// ---------------------------------------------------------------------------

// A dated person-in-unit record. A move-out sets moved_out; it NEVER deletes
// the row, so a unit has a history and "who lived in 4B in March" stays
// answerable.
export interface Occupancy {
  id: string;
  unit_id: number;
  // Present only when the router had the unit loaded — the roster and the unit
  // history fill it, PUT /occupancies/:id does not, and JSON.stringify drops
  // the key entirely rather than sending null.
  unit_number?: string;
  building_id: number;
  user_id: string | null;
  // PLAINTEXT, unlike users.full_name: the 018 backfill runs in plpgsql, which
  // cannot AES-GCM encrypt, and the front desk searches these columns. No
  // decrypt step is needed or possible on the client.
  first_name: string;
  last_name: string;
  relationship: OccupancyRelationship;
  // At most ONE open primary per unit (uq_occ_primary_open, partial on
  // moved_out IS NULL). A closed row sits outside the index, so history can
  // hold several past primaries.
  is_primary: boolean;
  moved_in: IsoDate;
  moved_out: IsoDate | null;
  // Server-derived (moved_out === null) so the client never reimplements "is
  // this person here now".
  is_current: boolean;
  lease_start: IsoDate | null;
  lease_end: IsoDate | null;
  move_out_reason: string | null;
}

// GET /units/:unitId/occupancies — full history, newest first, closed rows
// included.
export interface UnitOccupancyHistory {
  unit_id: number;
  unit_number: string;
  building_id: number;
  occupancies: Occupancy[];
}

// One open arrival — somebody who signed IN and has no departure recorded.
// A row of visitor_log with departed_at IS NULL.
export interface VisitorInside {
  id: number;
  // Decrypted server-side: the names are AES-GCM with a random IV, so this is
  // the only place they exist in readable form. May be empty when the entry was
  // saved without one (a delivery driver, a contractor).
  people: string[];
  visitor_type: string | null;
  // The unit as it was TYPED at sign-in — free text, not a units row, so it can
  // disagree with unit_id and is shown as written.
  unit_label: string | null;
  unit_id: number | null;
  headcount: number | null;
  arrived_at: IsoDate;
  // The arrival on the BUILDING's clock ('YYYY-MM-DD HH:mm'), already converted
  // server-side using properties.timezone. Never re-derive it from arrived_at
  // in the browser: the desk's machine can be in a different zone from the
  // building it is standing in.
  arrived_local: string;
  // TRUE = this arrival is from a PREVIOUS local day. Nobody recorded them
  // leaving and nobody has confirmed they are still here. It must never be
  // rendered the same way as somebody who walked in on this shift.
  stale: boolean;
  // Whole local days since the arrival's day. 0 = arrived today.
  days_open: number;
}

// GET /buildings/:buildingId/presence (alias: .../roster) — WHO'S HOME.
//
// TWO SECTIONS, NEVER ONE LIST AND NEVER ONE NUMBER. `residents` are open
// occupancies (people who LIVE here); `visitors_inside` are open arrivals
// (people who WALKED IN and were never signed out). They are different kinds of
// fact, they are wrong in different ways, and a total of the two answers no
// question anybody asked. Both halves are read in one server handler at one
// instant — `as_of` — so the screen cannot show a mix of two moments.
export interface BuildingRoster {
  building_id: number;
  building_name: string;
  // Non-archived units in the building, placeholder (kind='unassigned') ones
  // included. It exists so an empty roster can say WHICH kind of empty it is:
  // 0 means the building has no units yet, > 0 means it has doors and nobody
  // is checked in behind them. Not the same number as units_count on
  // GET /dashboard/buildings, which excludes placeholders.
  unit_count: number;
  count: number;
  residents: Occupancy[];
  // The single instant BOTH sections describe.
  as_of?: IsoDate;
  // THE CLOCK THIS ANSWER IS ON, decided by who asked (R-T12): the building's
  // for a concierge, the reader's own device for an owner or a manager.
  // `arrived_local` is rendered on it.
  timezone?: string;
  // WHOSE clock `timezone` is. 'building' — the building's own zone. 'viewer' —
  // the reader's device: what EVERY owner and manager is answered, and what a
  // concierge is answered only when the building has no usable zone.
  // 'fallback' — nothing usable; Greenwich.
  timezone_source?: 'building' | 'viewer' | 'fallback';
  // The same two, under the names every zone-decided response uses.
  tz?: string;
  tz_source?: 'building' | 'viewer' | 'fallback';
  // The start of the BUILDING's current day — what `stale` is measured from,
  // for everybody — and the zone it was cut in (always the building's own).
  local_day_start?: IsoDate;
  day_tz?: string;
  // null/undefined = the caller may NOT see visitor rows (no visitorlog:read),
  // so the section is hidden rather than shown empty. [] = allowed, and nobody
  // is inside. Those are different answers.
  visitors_inside?: VisitorInside[] | null;
  visitors_inside_count?: number | null;
  // How many of visitors_inside_count are `stale`. Beside the total, never
  // subtracted from it: both numbers are true and they answer different
  // questions ("how many open arrivals" vs "how many did anyone actually see").
  visitors_inside_stale_count?: number | null;
}

// POST /units/:unitId/occupancies (move-in).
//
// Either a user_id OR a first_name + last_name is enough: with a user_id the
// server fills missing names from that user's profile. moved_in defaults to
// today. is_primary left undefined means "take the slot only if it is free";
// passing true explicitly DISPLACES the current primary.
export interface CreateOccupancyInput {
  user_id?: string | null;
  first_name?: string;
  last_name?: string;
  relationship?: OccupancyRelationship;
  is_primary?: boolean;
  moved_in?: IsoDate;
  moved_out?: IsoDate | null;
  lease_start?: IsoDate | null;
  lease_end?: IsoDate | null;
}

// PUT /occupancies/:id. unit_id and building_id are rejected outright (400):
// relocation is a move-out plus a new occupancy, not an edit, because mutating
// unit_id would erase the fact that the person ever lived in the old unit.
// moved_in may be corrected but not cleared.
export interface UpdateOccupancyInput extends CreateOccupancyInput {
  move_out_reason?: string | null;
}

// POST /occupancies/:id/move-out. moved_out defaults to today.
export interface MoveOutInput {
  moved_out?: IsoDate;
  move_out_reason?: string | null;
}

// ---------------------------------------------------------------------------
// Access grants and scope
// ---------------------------------------------------------------------------

// A row of access_grants — the WRITE side of staff scoping. Writing a grant
// fires trg_grant_changed, which re-expands it into the derived access_scope
// read model; access_scope is never written directly.
export interface AccessGrant {
  id: string;
  user_id: string;
  scope_type: ScopeType;
  // Exactly one of these three is non-null, matching scope_type (chk_ag_shape);
  // an 'organization' grant leaves all three null. The *_name / unit_number
  // companions are resolved by the router for display and are null when the id
  // is null or the target row has since gone.
  portfolio_id: number | null;
  portfolio_name: string | null;
  building_id: number | null;
  building_name: string | null;
  unit_id: number | null;
  unit_number: string | null;
  can_operate: boolean;
  // The right to change org structure inside the scope. Only an owner may
  // grant it, enforced separately from the scope:manage permission.
  can_administer: boolean;
  reason: GrantReason;
  granted_by: string | null;
  valid_from: IsoDateTime;
  // null means open-ended. Revoking by setting this frees the uq_ag_open slot.
  valid_until: IsoDateTime | null;
  // Server-derived. Only open grants are expanded into access_scope, so this —
  // not valid_until alone — is what says whether a listed grant is doing
  // anything right now.
  is_open: boolean;
}

// POST /staff/:userId/scopes echoes back how many buildings the new grant
// actually resolved to, read from access_scope after the trigger fired. A
// portfolio grant reporting 12 buildings is the number an admin sanity-checks.
export interface AccessGrantCreated extends AccessGrant {
  resolved_building_count: number;
}

// One entry per building the user can actually reach, collapsed from the
// access_scope closure. A user may reach the same building through several
// grants (an org grant plus a building grant); the effective right is the
// union, which is what these flags already are.
export interface ResolvedBuilding {
  building_id: number;
  building_name: string | null;
  portfolio_id: number | null;
  portfolio_name: string | null;
  can_operate: boolean;
  can_administer: boolean;
  // Populated only by unit-scoped grants; empty for portfolio/building/org
  // grants, which cover the whole building.
  unit_ids: number[];
}

// name and email are AES-GCM columns run through tryDecrypt, which returns
// null when the value cannot be decrypted — so neither is guaranteed.
// The three ways a person reaches every building in the organisation without
// holding a grant that says so. Named rather than a bare boolean because the
// screen has to say WHY — "Owner" and "scope enforcement is off for this
// organisation" are very different facts to put in front of an admin.
export type OrgWideReason = 'owner' | 'organization_grant' | 'enforcement_off';

// Carried by every surface that renders access, because building_count alone
// cannot express it: an owner holds zero grants and zero scope rows and reaches
// everything, and so does every staff member in an org with scope enforcement
// off. Reading the counts without this field is what produced the "reaches no
// building" warning on the owner's own row.
export interface OrgWideAccess {
  org_wide: boolean;
  org_wide_reason: OrgWideReason | null;
}

export interface ScopedStaffUser extends OrgWideAccess {
  id: string;
  name: string | null;
  email: string | null;
  role: string;
  role_label: string;
  status: string | null;
}

// GET /staff/:userId/scopes. Both halves are returned because they answer
// different questions: `grants` is what an admin edits, `resolved_buildings` is
// what the user can actually reach.
export interface StaffScopes {
  user: ScopedStaffUser;
  grants: AccessGrant[];
  resolved_buildings: ResolvedBuilding[];
}

// GET /staff/scopes/summary row. building_count === 0 on an active staff member
// is what this screen exists to surface — they can sign in and see nothing —
// but ONLY when org_wide is false. An owner is 0/0 and reaches everything.
export interface ScopeSummary extends OrgWideAccess {
  user_id: string;
  name: string | null;
  email: string | null;
  role: string;
  role_label: string;
  status: string | null;
  grant_count: number;
  building_count: number;
}

// POST /staff/:userId/scopes. Supply the one id column that matches scope_type
// and omit the others. reason defaults to 'assignment'.
export interface CreateAccessGrantInput {
  scope_type: ScopeType;
  portfolio_id?: number;
  building_id?: number;
  unit_id?: number;
  can_operate?: boolean;
  can_administer?: boolean;
  reason?: GrantReason;
}

// ---------------------------------------------------------------------------
// Plain acknowledgements
// ---------------------------------------------------------------------------

export interface MessageResponse {
  message: string;
}

// DELETE /units/:id echoes the id, for both the archive and the delete path.
export interface UnitDeleteResponse extends MessageResponse {
  id: number;
}

// DELETE /staff/scopes/:grantId
export interface GrantRevokeResponse extends MessageResponse {
  id: string;
}
